<template>
  <div class="w-full">
    <div
      v-if="Object.keys(consumerPrograms).length > 0 || slots.leading"
      class="w-full space-y-2"
    >
      <div class="flex items-center justify-between gap-1">
        <h2 class="text-sm border-r leading-none pr-1">
          Programmes
        </h2>
        <NotificationProgramAdd
          v-if="item.customer"
          @add-program="showAddProgramInMainWindow(item.customer!.id)"
          @add-pet="showAddPetInMainWindow(item.customer!.id)"
        />
      </div>
      <div
        ref="programs"
      >
        <!-- What heads the list, before every consumer's programmes: the
             ADAC programme's card, once ADAC is asked about the patient. -->
        <div
          v-if="slots.leading"
          class="mb-3"
        >
          <slot name="leading" />
        </div>
        <div
          v-for="consumerKey in Object.keys(consumerPrograms)"
          :key="`program-${consumerKey}`"
        >
          <!-- The per-person label is redundant when a single consumer is displayed:
               the header already names the selected person. -->
          <p
            v-if="!selectedConsumerKey && !!getConsumer(consumerKey) && (getConsumer(consumerKey) as Consumer).type === 'beneficiary' "
            class="text-sm flex line-clamp-1"
          >
            {{ getConsumer(consumerKey)?.information.name }}
          </p>
          <div v-if="item.customer" class=" flex bg-white flex-wrap gap-3 items-start">
            <div
              v-for="(program, index) in consumerPrograms[consumerKey]"
              :key="`program-${program.id}-consumer-${consumerKey}-${index}`"
              :class="{
                'w-full': layoutComponent(index) === NotificationProgramLogoAdvanced,
              }"
            >
              <AnimatePresence>
                <motion.div
                  v-if="!hiddenPrograms.includes(program.id)"
                  :key="'program' + program.id"
                  :initial="{ opacity: 1, x: 0 }"
                  :exit="{ opacity: 0, x: 220 }"
                  :transition="{ duration: 0.25, type: 'tween' }"
                  @animation-complete="$emit('resize')"
                >
                  <component
                    :is="layoutComponent(index)"
                    v-if="getConsumer(consumerKey)"
                    :customer="item.customer"
                    :program="program"
                    :consumer="getConsumer(consumerKey) as Consumer"
                    @resize="$emit('resize')"
                    @mouseenter="notificationHovered(program, consumerKey)"
                    @show-program="showProgram(consumerKey, program)"
                    @hidden="hiddenPrograms.push(program.id)"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
    <p v-else class="text-sm">
      Ce patient n'est ciblé par aucun programme.
      <button
        v-if="item.customer"
        @click="showAddProgramInMainWindow(item.customer.id)"
      >
        <Icon name="mdi-plus-box-multiple" :size="16" color="gray-300" />
      </button>
    </p>
  </div>
</template>

<script lang="ts" setup>
import {computed, ref, useSlots, watch} from "vue";
import NotificationProgramLogoAdvanced
    from '@/program/components/program-logo/NotificationProgramLogoAdvanced.vue';
import NotificationProgramLogoSimple
    from '@/program/components/program-logo/NotificationProgramLogoSimple.vue';
import {pathwayApi} from '@/container';
import {Program} from "@/program/interfaces/Program";
import NotificationDetectionService from "@/notification/service/NotificationProgramService";
import {UserSettings} from "@/settings/interfaces";
import {DetectionNotificationProps, DetectionPatient} from "@/notification/interfaces";
import Consumer from "@/core/interfaces/consumers/Consumer";
import Icon from "@/core/components/icon/Icon.vue";
import {AnimatePresence, motion} from "motion-v";
import NotificationProgramAdd
    from "@/notification/components/notification-detection/programs/NotificationProgramAdd.vue";

const props = defineProps<{
    item: {
        patient: DetectionPatient;
        customer: DetectionNotificationProps["customer"];
        settings: UserSettings;
    },
    /** When set, only that consumer's programs are displayed (beneficiary ↔ insured switch). */
    selectedConsumerKey?: string | null
    /** Programmes left out of the list: the ones a card of their own stands for. */
    hiddenProgramIds?: number[]
}>();

const slots = useSlots();

const hiddenPrograms = ref<number[]>([]);

defineEmits(['resize']);
const maximumProgramsToDisplayPerConsumer = 3;

/**
 * The notification consumers.
 */
const consumers = computed(() => NotificationDetectionService.consumers(props.item.customer));

/**
 * The consumer programs.
 */
const consumerPrograms = computed(() => {
    const programs = NotificationDetectionService.consumerPrograms(props.item.customer, props.selectedConsumerKey);
    const left = props.hiddenProgramIds ?? [];

    if (!left.length) {
        return programs;
    }

    // A consumer with nothing left drops out, so the « no programme » line
    // shows when the left-out programme was the only one.
    return Object.fromEntries(Object.entries(programs)
        .map(([key, list]) => [key, list.filter(program => !left.includes(program.id))])
        .filter(([, list]) => list.length > 0));
});

/**
 * The layout component to use.
 */
const layoutComponent = (index: number) => {
    return props.item.settings.notification.layout === 'advanced'
    && (index - hiddenPrograms.value.length) < maximumProgramsToDisplayPerConsumer
        ? NotificationProgramLogoAdvanced
        : NotificationProgramLogoSimple;
};

/**
 * Get the consumer by key.
 */
const getConsumer = (key: string): Consumer | undefined => {
    return consumers.value.find(consumer => consumer.key === key);
};

/**
 * Show the program.
 */
const showProgram = (consumerKey: string, program: Program) => {
    if (!props.item.customer) {
        return;
    }

    if (program.programmable_type === 'discount') {
        window.ipcRenderer.send(
            'navigate',
            `/brand/${program.organization.entity_id}/offer/discount/${program.programmable_id}`,
            true
        );
    }

    if (program.programmable_type === 'support') {
        if (program.next_step) {
            pathwayApi().track('notification-click', program.next_step.pathway_id, consumerKey);
        }

        window.ipcRenderer.sendToMainWindow('show-support-program', {
            customerId: props.item.customer.id,
            programId: program.id,
            consumerKey
        });
    }

    if (program.programmable_type === 'feature') {
        window.ipcRenderer.send(
            'navigate',
            `/customer/${props.item.customer.id}/feature-programs/${program.id}/c_${props.item.customer.id}`,
            true
        );
    }
};

/**
 * Open the add program dialog in the main window, for the displayed consumer: the
 * selected beneficiary when the header switch is on one, the insured customer
 * otherwise. The dialog is keyed by consumer, so the inclusion cannot land on
 * another member of the household.
 */
const showAddProgramInMainWindow = (customerId: number) => {
    const target = props.selectedConsumerKey ? getConsumer(props.selectedConsumerKey) : undefined;
    const consumerKey = target?.key ?? props.item.customer!.key;
    const beneficiaryId = target?.type === 'beneficiary' ? target.id : undefined;

    window.ipcRenderer.click();
    window.ipcRenderer.send('to-main-window', 'show-customer', customerId, {addProgramDialog: consumerKey}, beneficiaryId);
};

/**
 * Show the customer in main window.
 */
const showAddPetInMainWindow = (customerId: number) => {
    window.ipcRenderer.click();
    window.ipcRenderer.send('navigate', '/customer/' + customerId + '/pets', false);
};

/**
 * If support program is hovered,
 * notify the electron main process to display the consumer program page on the main window.
 */
const notificationHovered = (program: Program, consumerKey: string) => {
    if (program.programmable_type === "support" && props.item.customer) {
        window.ipcRenderer.supportProgramNotificationHovered(props.item.customer.id, program.id, consumerKey);
    }
};

watch(() => props.item.customer, () => {
    hiddenPrograms.value = [];
}, {deep: true});
</script>
